import React, { RefCallback, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import JsonEditor from "@/components/general/jsonEditor";
import { requiredRule } from "@/components/tables/fields/fieldRules";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";

export interface CrudJsonEditorFieldProps {
  field: Pick<
    CrudModalField,
    "id" | "required" | "errorMessage" | "hideField" | "jsonEditorParameters"
  >;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (value: unknown) => void;
}

// empty values open as an empty object, unparsable text is shown as it is
export const toJsonEditorValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

const CrudJsonEditorField = ({
  field,
  validationTargetRef,
  onChange,
}: CrudJsonEditorFieldProps) => {
  const { control } = useFormContext();
  const [jsonInvalid, setJsonInvalid] = useState(false);

  if (field.hideField) return null;

  return (
    <Controller
      name={field.id as string}
      control={control}
      rules={{
        ...requiredRule(field.required, field.errorMessage),
        validate: { jsonValid: () => !jsonInvalid },
      }}
      render={({ field: { onChange: setFormValue, value } }) => (
        <div ref={validationTargetRef}>
          <JsonEditor
            label={field.jsonEditorParameters?.label}
            noLabel={field.jsonEditorParameters?.noLabel}
            mandatory={field.jsonEditorParameters?.mandatory}
            noTopBar={field.jsonEditorParameters?.noTopBar}
            noBottomBar={field.jsonEditorParameters?.noBottomBar}
            value={toJsonEditorValue(value)}
            onSave={(updatedJson) => {
              onChange?.(updatedJson);
              setFormValue(updatedJson);
            }}
            onEditorMount={(editor: any) => {
              const validate = () => {
                try {
                  JSON.parse(editor.getValue());
                  setJsonInvalid(false);
                } catch {
                  setJsonInvalid(true); 
                }
              };
              validate();
              editor.on("change", () => {
                try {
                  const parsed = JSON.parse(editor.getValue());
                  setJsonInvalid(false);
                  onChange?.(parsed);
                  setFormValue(parsed);
                } catch {
                  setJsonInvalid(true);
                }
              });
            }}
          />
        </div>
      )}
    />
  );
};

export default CrudJsonEditorField;
